import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Box,
  VStack,
  Text,
  Grid,
  GridItem,
  Heading,
  useToast,
} from '@chakra-ui/react';

import TopInfoSectionCard from './components/TopInfoSectionCard';
import VerifiedSellerContent from './components/VerifiedSellerContent';
import SettingsSectionCard from './components/SettingsSectionCard';
import MappingUploadSection from './components/MappingUploadSection';
import ListingFieldsConfigSection from './components/ListingFieldsConfigSection';
import ProcessProgressSection from './components/ProcessProgressSection';
import PartslinkScopeSection from './components/PartslinkScopeSection';
import ProcessControlPanel from './components/ProcessControlPanel';

import { PROCESS_STEPS } from './constants/mockData';
import { resolveListingFieldsConfig } from './utils/listingFieldsConfig.util';
import { getEntityId, getEntityName } from './utils/entityOptions.util';
import { parsePartslinkCsv } from './utils/parsePartslinkCsv.util';

import { getStoresListApi } from 'features/stores/stores.api';
import {
  getCreationSettingsApi,
  updateCreationSettingsApi,
  getCreationProcessStatusApi,
  getCreationMappingInfoApi,
  getCreationScopeCountApi,
  uploadCreationMappingApi,
  startCreationRunApi,
  stopCreationRunApi,
  getCreationStorePolicyReadinessApi,
  downloadCreationRunReportCsvApi,
} from 'features/listing-creation/listing-creation.api';
import { setProcessStatusFromSocket } from 'features/listing-creation/listing-creation.slice';
import socketCreation from '../../app/socketCreation';

const mapRunStepsToUi = (runState) => {
  const backendSteps = runState?.steps ?? [];
  if (backendSteps.length > 0) {
    return backendSteps.map((step) => {
      const status = step.status ?? 'idle';
      const percentage =
        status === 'completed' ? 100 : status === 'running' ? 50 : status === 'error' ? 100 : 0;
      const counts = step.counts ?? { total: 0, succeeded: 0, failed: 0 };
      const stepProgress =
        counts.total > 0
          ? Math.round((counts.succeeded / counts.total) * 100)
          : percentage;
      return {
        id: step.id,
        label: step.label,
        description: step.description,
        status,
        percentage: stepProgress,
        message: step.message,
        counts,
      };
    });
  }
  return PROCESS_STEPS.map((template) => ({
    ...template,
    status: 'idle',
    percentage: 0,
  }));
};

const ListingCreation = () => {
  const dispatch = useDispatch();
  const toast = useToast();

  const { list: storeOptions } = useSelector((state) => state.stores);
  const {
    settings,
    mappingInfo,
    scopeCount,
    scopeCountDetail,
    getScopeCountStatus,
    processStatus,
    isCreationRunning,
    downloadRunReportStatus,
    updateSettingsStatus,
  } = useSelector((state) => state.listingCreation);

  const [activeTopPanel, setActiveTopPanel] = useState(null);
  const [activeSettingsPanel, setActiveSettingsPanel] = useState(null);

  const [scopeMode, setScopeMode] = useState('manual');
  const [partslinkCsvFile, setPartslinkCsvFile] = useState(null);
  const [parsedCsvPartslinks, setParsedCsvPartslinks] = useState([]);
  const [manualPartslinks, setManualPartslinks] = useState('');
  const [selectedStoreId, setSelectedStoreId] = useState('');
  const [storePolicyReadiness, setStorePolicyReadiness] = useState(null);
  const [storePolicyReadinessLoading, setStorePolicyReadinessLoading] = useState(false);

  const [mappingFile, setMappingFile] = useState(null);

  const savedItemLocation = (settings?.listingItemLocation ?? '').trim();

  const mappingReady = Boolean(mappingInfo?.exists || mappingFile);

  const listingFieldsConfig = useMemo(
    () => resolveListingFieldsConfig(settings?.listingFieldsConfig),
    [settings?.listingFieldsConfig],
  );

  const verifiedSeller = useMemo(
    () =>
      settings?.verifiedSeller ?? {
        username: settings?.verifiedSellerUsername ?? 'carpartswholesale',
        label: 'Car Parts Wholesale',
      },
    [settings],
  );

  const manualPartslinkList = useMemo(
    () =>
      manualPartslinks
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean),
    [manualPartslinks],
  );

  const scopePartslinks = useMemo(() => {
    if (scopeMode === 'csv') return parsedCsvPartslinks;
    return manualPartslinkList;
  }, [scopeMode, parsedCsvPartslinks, manualPartslinkList]);

  const scopeReady =
    scopeMode === 'csv' ? parsedCsvPartslinks.length > 0 : manualPartslinkList.length > 0;

  const steps = useMemo(() => mapRunStepsToUi(processStatus), [processStatus]);
  const overallProgress = processStatus?.overallProgress ?? 0;
  const displayIsRunning = isCreationRunning || Boolean(processStatus?.isRunning);
  const progressMessage = processStatus?.message ?? '';
  const runSummary = processStatus?.runSummary ?? null;
  const hasRunReport = Boolean(processStatus?.hasReport);

  const estimatedPartslinkCount = useMemo(() => {
    if (getScopeCountStatus === 'succeeded' && scopeCount != null) {
      return scopeCount;
    }
    return null;
  }, [scopeCount, getScopeCountStatus]);

  const savedMappingMeta = useMemo(() => {
    if (!mappingInfo?.exists) return null;
    return {
      exists: true,
      fileName: mappingInfo.fileName,
      uploadedAt: mappingInfo.uploadedAt,
      fileSizeBytes: mappingInfo.fileSizeBytes,
      storedEntryCount: mappingInfo.storedEntryCount ?? mappingInfo.entryCount,
      entryCount: mappingInfo.entryCount,
    };
  }, [mappingInfo]);

  const readinessItems = useMemo(() => {
    const items = [
      {
        id: 'mapping',
        label: mappingReady
          ? `Supplier SKU → Partslink mapping uploaded${
              mappingInfo?.storedEntryCount
                ? ` (${mappingInfo.storedEntryCount.toLocaleString()} rows)`
                : ''
            }`
          : 'Upload a supplier SKU → Partslink mapping CSV',
        met: mappingReady,
      },
      {
        id: 'scope',
        label:
          scopeMode === 'csv'
            ? parsedCsvPartslinks.length > 0
              ? `${parsedCsvPartslinks.length} partslink${parsedCsvPartslinks.length === 1 ? '' : 's'} from CSV`
              : partslinkCsvFile
                ? 'Parsing CSV…'
                : 'Upload a partslink CSV for this run'
            : manualPartslinkList.length > 0
              ? `${manualPartslinkList.length} partslink${manualPartslinkList.length === 1 ? '' : 's'} in manual list`
              : 'Add at least one partslink to the manual list',
        met: scopeReady,
      },
    ];

    const store = storeOptions?.find((s) => getEntityId(s) === selectedStoreId);
    if (!selectedStoreId) {
      items.push({
        id: 'store',
        label: 'Select a store (used for eBay API access)',
        met: false,
      });
    } else if (storePolicyReadinessLoading) {
      items.push({
        id: 'store',
        label: `Store${store ? `: ${getEntityName(store)}` : ''} — checking default eBay policies…`,
        met: false,
      });
    } else if (!storePolicyReadiness?.ready) {
      items.push({
        id: 'store',
        label:
          storePolicyReadiness?.error ||
          `Store${store ? ` ${getEntityName(store)}` : ''} is missing default eBay policies — configure fulfillment, payment, and return on the store policies tab`,
        met: false,
      });
    } else {
      items.push({
        id: 'store',
        label: `Store${store ? `: ${getEntityName(store)}` : ''} — default eBay policies configured`,
        met: true,
      });
    }

    items.push({
      id: 'location',
      label: savedItemLocation
        ? `Item location: ${savedItemLocation}`
        : 'Set and save item location in listing fields configuration',
      met: Boolean(savedItemLocation),
    });

    return items;
  }, [
    mappingReady,
    mappingInfo,
    scopeMode,
    parsedCsvPartslinks.length,
    partslinkCsvFile,
    manualPartslinkList.length,
    scopeReady,
    selectedStoreId,
    storeOptions,
    savedItemLocation,
    storePolicyReadiness,
    storePolicyReadinessLoading,
  ]);

  const canStart = readinessItems.every((item) => item.met) && !displayIsRunning;

  const progressSummaryMeta = useMemo(() => {
    if (displayIsRunning) {
      return { label: `${overallProgress}% running`, colorScheme: 'blue' };
    }
    if (overallProgress >= 100 && !progressMessage?.toLowerCase().includes('interrupted')) {
      return { label: 'Last run completed', colorScheme: 'green' };
    }
    if (overallProgress > 0 || processStatus?.finishedAt) {
      const interrupted = progressMessage?.toLowerCase().includes('interrupted');
      return {
        label: interrupted ? `${overallProgress}% interrupted` : `${overallProgress}% last run`,
        colorScheme: interrupted ? 'orange' : 'teal',
      };
    }
    return { label: null, colorScheme: 'teal' };
  }, [displayIsRunning, overallProgress, progressMessage, processStatus?.finishedAt]);

  useEffect(() => {
    dispatch(getStoresListApi());
    dispatch(getCreationSettingsApi());
    dispatch(getCreationMappingInfoApi());
    dispatch(getCreationProcessStatusApi());
  }, [dispatch]);

  useEffect(() => {
    if (!selectedStoreId) {
      setStorePolicyReadiness(null);
      setStorePolicyReadinessLoading(false);
      return undefined;
    }

    let cancelled = false;
    setStorePolicyReadinessLoading(true);

    dispatch(getCreationStorePolicyReadinessApi(selectedStoreId)).then((result) => {
      if (cancelled) return;
      if (getCreationStorePolicyReadinessApi.fulfilled.match(result)) {
        setStorePolicyReadiness(result.payload);
      } else {
        setStorePolicyReadiness({
          ready: false,
          error: result.payload?.message || 'Could not check store default eBay policies',
        });
      }
      setStorePolicyReadinessLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [dispatch, selectedStoreId]);

  useEffect(() => {
    if (scopeMode !== 'csv' || !partslinkCsvFile) {
      setParsedCsvPartslinks([]);
      return undefined;
    }

    let cancelled = false;
    parsePartslinkCsv(partslinkCsvFile)
      .then((rows) => {
        if (!cancelled) setParsedCsvPartslinks(rows);
      })
      .catch(() => {
        if (!cancelled) setParsedCsvPartslinks([]);
      });

    return () => {
      cancelled = true;
    };
  }, [scopeMode, partslinkCsvFile]);

  const scopeCountParams = useMemo(
    () => ({
      scopeMode,
      partslinks: scopePartslinks,
      limit: settings?.maxPartslinksPerRun ?? 500,
    }),
    [scopeMode, scopePartslinks, settings?.maxPartslinksPerRun],
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      if (scopePartslinks.length === 0) return;
      dispatch(getCreationScopeCountApi(scopeCountParams));
    }, 350);
    return () => clearTimeout(timer);
  }, [dispatch, scopeCountParams, scopePartslinks.length]);

  useEffect(() => {
    socketCreation.connect();
    socketCreation.on('creation-run-state', (payload) => {
      dispatch(setProcessStatusFromSocket(payload));
    });
    socketCreation.on('creation-status', () => {
      dispatch(getCreationProcessStatusApi());
    });
    return () => {
      socketCreation.off('creation-run-state');
      socketCreation.off('creation-status');
      socketCreation.disconnect();
    };
  }, [dispatch]);

  useEffect(() => {
    if (!displayIsRunning) return undefined;
    const interval = setInterval(() => {
      dispatch(getCreationProcessStatusApi());
    }, 2000);
    return () => clearInterval(interval);
  }, [dispatch, displayIsRunning]);

  const handleDownloadRunReport = () => {
    dispatch(downloadCreationRunReportCsvApi());
  };

  const handleSaveListingFields = async ({ listingFieldsConfig, listingItemLocation }) => {
    const result = await dispatch(
      updateCreationSettingsApi({ listingFieldsConfig, listingItemLocation }),
    );
    if (updateCreationSettingsApi.fulfilled.match(result)) {
      toast({
        title: 'Configuration saved',
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
      dispatch(getCreationSettingsApi());
      return true;
    }
    toast({
      title: 'Save failed',
      description: result.payload?.message || 'Could not update settings',
      status: 'error',
      duration: 5000,
      isClosable: true,
    });
    return false;
  };

  const handleMappingUpload = async (file) => {
    if (!file) return;
    setMappingFile({ name: file.name, size: file.size });
    const result = await dispatch(uploadCreationMappingApi(file));
    if (uploadCreationMappingApi.fulfilled.match(result)) {
      toast({
        title: 'Mapping uploaded',
        description: `${result.payload?.entryCount ?? 0} rows saved`,
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
      dispatch(getCreationMappingInfoApi());
    } else {
      toast({
        title: 'Upload failed',
        description: result.payload?.message || 'Could not save mapping file',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleStart = async () => {
    setActiveSettingsPanel('progress');

    let partslinks = scopePartslinks;
    if (scopeMode === 'csv' && partslinkCsvFile) {
      try {
        partslinks = await parsePartslinkCsv(partslinkCsvFile);
      } catch {
        toast({
          title: 'Could not parse CSV',
          description: 'Check the file format and try again',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return;
      }
    }

    if (partslinks.length === 0) {
      toast({
        title: 'No partslinks in scope',
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    const result = await dispatch(
      startCreationRunApi({
        storeId: selectedStoreId,
        scopeMode,
        partslinks,
        maxPartslinks: settings?.maxPartslinksPerRun ?? 500,
        listingItemLocation: savedItemLocation,
      }),
    );

    if (startCreationRunApi.fulfilled.match(result)) {
      if (result.payload?.processStatus) {
        dispatch(setProcessStatusFromSocket(result.payload.processStatus));
      }
      toast({
        title: 'Listing creation started',
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
      dispatch(getCreationProcessStatusApi());
    } else {
      toast({
        title: 'Could not start',
        description: result.payload?.message || 'Start failed',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleStop = async () => {
    await dispatch(stopCreationRunApi());
    toast({ title: 'Stop requested', status: 'info', duration: 3000, isClosable: true });
  };

  const renderSettingsPanelContent = () => {
    if (activeSettingsPanel === 'progress') {
      return (
        <ProcessProgressSection
          embedded
          steps={steps}
          overallProgress={overallProgress}
          isRunning={displayIsRunning}
          currentMessage={progressMessage}
          runSummary={runSummary}
          hasReport={hasRunReport}
          onDownloadReport={handleDownloadRunReport}
          isDownloadingReport={downloadRunReportStatus === 'loading'}
        />
      );
    }

    if (activeSettingsPanel === 'mapping') {
      return (
        <MappingUploadSection
          embedded
          mappingFile={mappingFile}
          savedMapping={savedMappingMeta}
          onMappingFileChange={setMappingFile}
          onFileSelected={handleMappingUpload}
          previewRows={
            mappingInfo?.sample?.map((r) => ({
              sku: r.supplierSku,
              partslink: r.partslink,
            })) ?? []
          }
        />
      );
    }

    if (activeSettingsPanel === 'fields') {
      return (
        <ListingFieldsConfigSection
          embedded
          listingFieldsConfig={listingFieldsConfig}
          listingItemLocation={settings?.listingItemLocation ?? ''}
          onSave={handleSaveListingFields}
          isSaving={updateSettingsStatus === 'loading'}
        />
      );
    }

    return null;
  };

  return (
    <Box p={6} mt="50px">
      <VStack spacing={6} align="stretch">
        <Box>
          <Heading size="lg" color="gray.700" mb={1}>
            Listing Creation
          </Heading>
          <Text color="gray.500" fontSize="sm">
            Create new catalog SKUs and marketplace listings from verified eBay reference data.
            Provide a partslink list, resolve supplier SKUs via mapping, then compile and publish
            using your listing field rules.
          </Text>
        </Box>

        <TopInfoSectionCard activePanel={activeTopPanel} onPanelChange={setActiveTopPanel}>
          {activeTopPanel === 'seller' && (
            <VerifiedSellerContent verifiedSeller={verifiedSeller} />
          )}
        </TopInfoSectionCard>

        <SettingsSectionCard
          activePanel={activeSettingsPanel}
          onPanelChange={setActiveSettingsPanel}
          progressSummary={progressSummaryMeta.label}
          progressBadgeColorScheme={progressSummaryMeta.colorScheme}
          mappingReady={mappingReady}
        >
          {renderSettingsPanelContent()}
        </SettingsSectionCard>

        <Grid templateColumns={{ base: '1fr', xl: '3fr 1fr' }} gap={4} alignItems="stretch">
          <GridItem>
            <PartslinkScopeSection
              scopeMode={scopeMode}
              onScopeModeChange={setScopeMode}
              partslinkCsvFile={partslinkCsvFile}
              onPartslinkCsvFileChange={setPartslinkCsvFile}
              manualPartslinks={manualPartslinks}
              onManualPartslinksChange={setManualPartslinks}
              selectedStoreId={selectedStoreId}
              onStoreChange={setSelectedStoreId}
              storeOptions={storeOptions ?? []}
            />
          </GridItem>
          <GridItem>
            <ProcessControlPanel
              isRunning={displayIsRunning}
              canStart={canStart}
              readinessItems={readinessItems}
              onStart={handleStart}
              onStop={handleStop}
              estimatedPartslinkCount={estimatedPartslinkCount}
              scopeCountDetail={scopeCountDetail}
              scopeCountLoading={getScopeCountStatus === 'loading'}
            />
          </GridItem>
        </Grid>
      </VStack>
    </Box>
  );
};

export default ListingCreation;
